import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import { Check, ChevronDown } from 'lucide-react'
import styles from 'components/panels/stream-order.module.scss'
import WithTooltip from 'components/shared/WithTooltip'
import useStreamFilter, { Filter, withDepth } from 'components/filters/useStreamFilters'
import { ordinalsStreamQueryFilter } from 'components/panels/ordinalsThreshold'
import useStore from 'state/knovStore'
import queryClient from 'api/queryClient'
import { PanelState } from 'state/PanelState'
import SearchIcon from 'components/shared/icons/SearchIcon'
import { canonicalizeQuestFilter } from 'lib/questFilterCanonicalization'
import { fixedHeaderEnabled } from 'lib/fixedHeader'
import {
    clearPanelViewControls,
    setPanelViewControls,
    type ViewOption,
} from 'lib/panelViewControls'
import {
    getDisplayedTopSelection,
    getStreamOrderQueryKey,
    getTopOrderEntry,
    getMarketOrderMapping,
    isTopOrder,
    isTopWindow,
    topOrderLabel,
    TOP_WINDOW_OPTIONS,
    TopOrderMeta,
    TopWindowKey,
    shouldInvalidateStreamOrderQuery,
} from 'components/panels/streamOrderMapping'

// Define and Export Stream Order Configurations
export const streamOrderConfigs = {
    upvote: {
        icon: 'fa fa-arrow-up fa-large',
        label: 'Top',
        tip: 'Messages sorted by upvotes',
    },
    upvoteTrending: {
        icon: 'fa fa-fire',
        label: 'Hot',
        tip: 'Messages sorted by recency and upvotes',
    },
    upvalue: {
        //icon: 'fa fa-bitcoin fa-large',
        icon: 'fa fa-arrow-up fa-large',
        label: 'Top',
        tip: 'Messages sorted by upvalue',
    },
    upvalueTrending: {
        icon: 'fa fa-fire',
        label: 'Hot',
        tip: 'Messages sorted by recency and upvalue',
    },
    relevance: {
        icon: null, // Will use SearchIcon component
        label: 'Relev',
        tip: 'Messages sorted by relevance',
    },
    time: {
        icon: 'fa fa-clock-o',
        label: 'Now',
        tip: 'Recently created messages.',
    },
}

// Read the server-owned capability off gon so the pure getFilterMapping helper
// (used outside React too) can gate TOP NEW without a hook.
const canTopNew = () => window.gon?.currentUser?.can_top_new ?? false

// Define and Export Filter Mapping Logic
export const getFilterMapping = (filter: Filter | undefined | null) => {
    const hasSearchQuery = !!filter?.query
    const isFollowing = filter?.following
    const isPublic = filter?.public || filter?.treechat || filter?.hodlocker
    const isPublicTeam =
        filter?.team_ids?.length > 0 &&
        useStore.getState().publicTeams?.some(team => filter?.team_ids?.includes(team.id))
    const isMeme = filter?.meme
    const isMemesDiscovery = filter?.memes
    const isUser = filter?.user
    const isOrdinals = !!filter?.ordinals || !!filter?.user_ordinals
    const isMarket = !!filter?.ordinals && filter?.market === true
    // Activity is a chronological card feed: the public New | Hot | Top set.
    const isMarketActivity = filter?.market === true && filter?.market_cards === true

    const topNewEnabled = canTopNew()
    const defaultTopOrder = getTopOrderEntry(topNewEnabled, 'upvote', streamOrderConfigs.upvote)
    const publicTopOrder = getTopOrderEntry(topNewEnabled, 'upvalue', streamOrderConfigs.upvalue)

    const defaultSearchOrder = {
        time: streamOrderConfigs.time,
        upvoteTrending: streamOrderConfigs.upvoteTrending,
        ...defaultTopOrder,
    }
    const publicOrder = {
        time: streamOrderConfigs.time,
        upvalueTrending: streamOrderConfigs.upvalueTrending,
        ...publicTopOrder,
    }
    // Following (feed_items aggregates) and private (ES) both support the full
    // New | Hot | Top set, matching the public feed.

    if (hasSearchQuery) {
        return { relevance: streamOrderConfigs.relevance }
    } else if (isMarket) {
        // Listings rank by listing time, asking price, or the wall's Top pill
        // (Top New included). The wall's Hot ranks ordinals, not asks.
        return getMarketOrderMapping(topNewEnabled)
    } else if (isOrdinals) {
        const ordinalTopOrder = getTopOrderEntry(topNewEnabled, 'upvalue', {
            ...streamOrderConfigs.upvalue,
            tip: 'Most upvalued ordinals',
        })
        // Owner-scoped streams (My Ordinals, a profile's grid) rank Now by when
        // the owner received each ordinal, so an imported collection does not
        // sit on top of every earlier mint.
        const isOwnerScoped = !!filter?.user_ordinals || filter?.ordinals_scope === 'mine'
        // The media stream honors TOP NEW (unseen tiering on lifetime value) and
        // the Top windows (creation-time cutoff, lifetime value).
        return {
            time: isOwnerScoped
                ? { ...streamOrderConfigs.time, tip: 'Most recently acquired first' }
                : streamOrderConfigs.time,
            upvalueTrending: {
                ...streamOrderConfigs.upvalueTrending,
                tip: 'Ordinals ranked by recent upvalue',
            },
            ...ordinalTopOrder,
        }
    } else if (isFollowing || filter?.private === true) {
        return { ...publicOrder }
    } else if (isMarketActivity) {
        return { ...publicOrder }
    } else if (isPublic || isPublicTeam || isMeme || isMemesDiscovery || isUser) {
        return { ...publicOrder }
    } else {
        return defaultSearchOrder
    }
}

interface StreamOrderProps {
    filter: Filter | undefined | null
    panelId: string
    panel: PanelState
    query?: string
}

const streamTypeForTracking = (filter: Filter | undefined | null) =>
    filter?.market && filter?.market_cards
        ? 'market_activity'
        : filter?.public
        ? 'public'
        : filter?.private
        ? 'private'
        : filter?.team_ids
        ? 'team'
        : filter?.ordinals && filter?.market
        ? 'market'
        : filter?.ordinals
        ? 'ordinals'
        : filter?.following
        ? 'following'
        : 'other'

type TopMenuOption = {
    key: string
    label: string
    tip: string
    order: string
    window: TopWindowKey | null
}

// The Top pill: a dropdown over TOP NEW (when enabled), the time-windowed Tops,
// and all-time Top. All options share one slot in the order row. Music's
// Discover order row uses it too.
export function TopOrderPill({
    meta,
    icon,
    fallbackLabel,
    isSelected,
    effectiveOrder,
    effectiveWindow,
    onSelect,
}: {
    meta: TopOrderMeta
    icon: string
    fallbackLabel: string
    isSelected: boolean
    effectiveOrder: string | undefined
    effectiveWindow: TopWindowKey | undefined
    onSelect: (order: string, topWindow: TopWindowKey | null) => void
}) {
    const [open, setOpen] = useState(false)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>()
    const pillRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                pillRef.current &&
                !pillRef.current.contains(event.target as Node)
            ) {
                setMenuStyle(undefined)
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    const toggleMenu = () => {
        if (open) {
            setMenuStyle(undefined)
            setOpen(false)
            return
        }
        const rect = pillRef.current?.getBoundingClientRect()
        if (!rect) return

        setMenuStyle({
            position: 'fixed',
            top: rect.bottom + 6,
            left: Math.max(8, Math.min(rect.left, window.innerWidth - 180)),
            zIndex: 1000,
        })
        setOpen(true)
    }

    const options: TopMenuOption[] = [
        ...(meta.topNewEnabled
            ? [
                  {
                      key: 'top_new',
                      label: 'Top New',
                      tip: 'Top-value threads you haven’t seen yet first',
                      order: 'top_new',
                      window: null,
                  },
              ]
            : []),
        ...TOP_WINDOW_OPTIONS.map(option => ({
            key: `${meta.standardOrder}:${option.window}`,
            label: option.label,
            tip: option.tip,
            order: meta.standardOrder,
            window: option.window,
        })),
        {
            key: meta.standardOrder,
            label: 'Top',
            tip: 'Top threads of all time',
            order: meta.standardOrder,
            window: null,
        },
    ]

    const isOptionActive = (option: TopMenuOption) =>
        isSelected &&
        (option.order === 'top_new'
            ? effectiveOrder === 'top_new'
            : effectiveOrder !== 'top_new' && (option.window ?? undefined) === effectiveWindow)

    // Selected: name the active choice. Unselected: the surface's default Top
    // label (Top New when enabled), matching the pre-dropdown pill.
    const pillLabel = isSelected ? topOrderLabel(effectiveOrder, effectiveWindow) : fallbackLabel

    // Split button: the label half applies exactly the option the pill face
    // reads (so re-selecting Top never requires the menu); the caret half
    // opens the window menu. Derive the option from the same inputs as
    // pillLabel so the click always matches what the user sees.
    const displayedSelection = getDisplayedTopSelection(
        isSelected,
        effectiveOrder,
        effectiveWindow,
        meta,
    )

    const selectDisplayed = (ev: React.MouseEvent) => {
        ev.stopPropagation()
        ev.preventDefault()
        if (open) {
            setMenuStyle(undefined)
            setOpen(false)
        }
        onSelect(displayedSelection.order, displayedSelection.window)
    }

    return (
        <>
            {/* Stable anchor for the menu's outside-click + position math.
                asChild tooltips put the tip props on the halves directly, so
                the disable flip while open never restructures the DOM. */}
            <div ref={pillRef} className={styles.topPillAnchor}>
                <div
                    className={cn(styles.orderBtn, styles.splitPill, isSelected && styles.selected)}
                >
                    <WithTooltip asChild tip={`Sort by ${pillLabel}`} disable={open}>
                        <button
                            type="button"
                            className={styles.splitSelect}
                            onClick={selectDisplayed}
                        >
                            <i className={icon} aria-hidden="true" />
                            <span className={styles.orderLabel}>{pillLabel}</span>
                        </button>
                    </WithTooltip>
                    <WithTooltip asChild tip="Choose a time window" disable={open}>
                        <button
                            type="button"
                            className={styles.splitCaret}
                            aria-label="Top sort options"
                            aria-haspopup="menu"
                            aria-expanded={open}
                            onClick={ev => {
                                ev.stopPropagation()
                                ev.preventDefault()
                                toggleMenu()
                            }}
                        >
                            <ChevronDown size={12} className={styles.topChevron} />
                        </button>
                    </WithTooltip>
                </div>
            </div>
            {open &&
                menuStyle &&
                ReactDOM.createPortal(
                    <div ref={menuRef} className={styles.topMenu} style={menuStyle} role="menu">
                        {options.map(option => {
                            const active = isOptionActive(option)
                            return (
                                <button
                                    key={option.key}
                                    type="button"
                                    role="menuitem"
                                    title={option.tip}
                                    className={cn(
                                        styles.topMenuItem,
                                        active && styles.topMenuItemActive,
                                    )}
                                    onClick={ev => {
                                        ev.stopPropagation()
                                        ev.preventDefault()
                                        setMenuStyle(undefined)
                                        setOpen(false)
                                        onSelect(option.order, option.window)
                                    }}
                                >
                                    <span>{option.label}</span>
                                    {active && <Check size={14} className={styles.topMenuCheck} />}
                                </button>
                            )
                        })}
                    </div>,
                    document.body,
                )}
        </>
    )
}

export default function StreamOrder({ panelId, filter, panel, query }: StreamOrderProps) {
    const { setStreamFilter } = useStreamFilter(panelId)

    // Use the exported logic
    const filterMapping = getFilterMapping(filter)
    const effectiveFilter = canonicalizeQuestFilter(filter)
    const effectiveOrder = effectiveFilter?.order
    const effectiveWindow = isTopWindow(effectiveFilter?.top_window)
        ? effectiveFilter?.top_window
        : undefined
    const defaultFilterKey = Object.keys(filterMapping)[0]

    const applyOrder = (order: string, topWindow: TopWindowKey | null) => {
        // Track every order item click, including an already-active item, so
        // each Top dropdown choice is represented in Amplitude.
        const currentOrder = effectiveOrder || defaultFilterKey
        const currentWindow = effectiveWindow || null
        const selectionChanged = currentOrder !== order || currentWindow !== topWindow

        window.logEv('CLICK_STREAM_ORDER', {
            order_type: order,
            top_window: topWindow || undefined,
            previous_order: currentOrder,
            previous_top_window: currentWindow || undefined,
            selection_changed: selectionChanged,
            stream_type: streamTypeForTracking(filter),
            is_search_active: !!filter?.query,
            has_query: !!query,
        })

        if (!selectionChanged) return

        const newFilter = { ...filter, order }
        if (topWindow) {
            newFilter.top_window = topWindow
        } else {
            // The window is a Top-only refinement; every other selection keeps
            // the filter canonical so query cache keys don't fork on a stale key.
            delete newFilter.top_window
        }

        // A missing destination query fetches when selected; a fresh one can be
        // reused. Mark only expired cached groups stale before activating them.
        // Ordinals keys also carry the resolved quality-dial stop.
        const queryFilter =
            filter?.ordinals || filter?.user_ordinals
                ? ordinalsStreamQueryFilter(withDepth(newFilter as Filter))
                : newFilter
        const queryKey = getStreamOrderQueryKey(queryFilter)
        if (shouldInvalidateStreamOrderQuery(queryClient.getQueryState(queryKey))) {
            queryClient.invalidateQueries({ queryKey, exact: true })
        }

        setStreamFilter(newFilter)
    }

    // fixed_header: publish the orders (Top flattened into its variants) so
    // the panel's top bar can switch them once this row scrolls away.
    useEffect(() => {
        if (!fixedHeaderEnabled() || !panelId) return
        const options: ViewOption[] = []
        let active: string | null = null
        const choices: Record<string, [string, TopWindowKey | null]> = {}
        Object.entries(filterMapping).forEach(([filterKey, filterValue]) => {
            const { icon, label } = filterValue as { icon: string | null; label: string }
            const topMeta = (filterValue as { top?: TopOrderMeta }).top
            if (topMeta) {
                const variants: Array<[string, string, TopWindowKey | null]> = [
                    ...(topMeta.topNewEnabled
                        ? ([['top_new', 'Top New', null]] as Array<[string, string, null]>)
                        : []),
                    ...TOP_WINDOW_OPTIONS.map(
                        o =>
                            [topMeta.standardOrder, o.label, o.window] as [
                                string,
                                string,
                                TopWindowKey,
                            ],
                    ),
                    [topMeta.standardOrder, 'Top', null],
                ]
                variants.forEach(([order, variantLabel, window]) => {
                    const key = `${order}:${window || ''}`
                    options.push({ key, label: variantLabel, icon })
                    choices[key] = [order, window]
                    const isActive =
                        !filter?.query &&
                        (order === 'top_new'
                            ? effectiveOrder === 'top_new'
                            : isTopOrder(effectiveOrder) &&
                              effectiveOrder !== 'top_new' &&
                              (window ? effectiveWindow === window : !effectiveWindow))
                    if (isActive) active = key
                })
                return
            }
            const key = `${filterKey}:`
            options.push({ key, label, icon: filterKey === 'relevance' ? null : icon })
            choices[key] = [filterKey, null]
            if (
                filter?.query ||
                effectiveOrder === filterKey ||
                (!effectiveOrder && filterKey === defaultFilterKey)
            )
                active = active || key
        })
        setPanelViewControls(panelId, {
            order: {
                options,
                active,
                select: key => choices[key] && applyOrder(choices[key][0], choices[key][1]),
            },
        })
    })
    useEffect(() => () => panelId && clearPanelViewControls(panelId, 'order'), [panelId])

    return (
        <div className={styles.streamOrderComp}>
            <div className={styles.orderBtnContainer}>
                {Object.entries(filterMapping).map(([filterKey, filterValue]) => {
                    const { icon, label, tip } = filterValue
                    const topMeta: TopOrderMeta | undefined = (
                        filterValue as { top?: TopOrderMeta }
                    ).top

                    if (topMeta) {
                        // Selected whenever the current order is any Top flavor,
                        // so a saved upvote/upvalue order still lights the pill
                        // even when the mapping keys the slot as top_new.
                        const isSelected =
                            !filter?.query &&
                            (isTopOrder(effectiveOrder) ||
                                (!effectiveOrder && filterKey === defaultFilterKey))
                        return (
                            <div key={label}>
                                <TopOrderPill
                                    meta={topMeta}
                                    icon={icon}
                                    fallbackLabel={label}
                                    isSelected={isSelected}
                                    effectiveOrder={effectiveOrder}
                                    effectiveWindow={effectiveWindow}
                                    onSelect={applyOrder}
                                />
                            </div>
                        )
                    }

                    // Determine the default key if filter.order is not set
                    const isSelected =
                        filter?.query ||
                        effectiveOrder === filterKey ||
                        (!effectiveOrder && filterKey === defaultFilterKey)
                    const orderBtnStyles = cn(styles.orderBtn, isSelected && styles.selected)
                    return (
                        <div key={label}>
                            <WithTooltip key={filterKey} tip={tip}>
                                <div
                                    className={orderBtnStyles}
                                    onClick={ev => {
                                        ev.stopPropagation()
                                        ev.preventDefault()
                                        applyOrder(filterKey, null)
                                    }}
                                >
                                    {filterKey === 'relevance' ? (
                                        <SearchIcon size={14} />
                                    ) : (
                                        <i className={icon} />
                                    )}
                                    <span className={styles.orderLabel}>{label}</span>
                                </div>
                            </WithTooltip>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
